/**
 * 登录页（CLAUDE.md §前端规范；LEO 设计 §登录页 Token）。
 *
 * 入口：用户首次进入系统（未登录时）显示此页；登录成功后跳转主界面。
 */

import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ApiError } from "../services/apiClient";
import { login as apiLogin } from "../services/auth";
import { useAuth } from "../contexts/AuthContext";
import { pushToast } from "../hooks/useToast";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fromPath =
    (location.state as { from?: string } | null)?.from ?? "/tencent";

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    if (!account.trim() || !password) {
      setErrorMsg("请输入账号与密码");
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const data = await apiLogin({ account: account.trim(), password });
      login(data.token, data.user_id);
      pushToast("登录成功", "success");
      navigate(fromPath, { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || err.suggestion || "登录失败"
          : "网络异常，请稍后重试";
      setErrorMsg(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="leo-card w-full max-w-[420px] rounded-2xl px-8 py-9">
        <div className="mb-6 flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent-cta/15 text-accent-cta-hover">
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 11l9-7 9 7" />
              <path d="M5 10v10h14V10" />
              <path d="M9 20v-6h6v6" />
            </svg>
          </span>
          <div>
            <div className="font-display text-[1.05rem] font-semibold text-text-primary">
              广告自动化投放系统
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-text-muted">
              Ad-Tech OS
            </div>
          </div>
        </div>

        <h1 className="font-display text-[1.4rem] font-bold leading-snug text-text-emphasis">
          欢迎回来
        </h1>
        <p className="mt-1.5 text-[12.5px] text-text-muted">
          请使用账号密码登录以继续
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label className="mb-1.5 block text-[11.5px] font-semibold uppercase tracking-[0.06em] text-text-strong">
              账号
            </label>
            <input
              type="text"
              className="leo-input"
              placeholder="请输入登录账号"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              autoComplete="username"
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11.5px] font-semibold uppercase tracking-[0.06em] text-text-strong">
              密码
            </label>
            <input
              type="password"
              className="leo-input"
              placeholder="请输入密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>

          {errorMsg ? (
            <div className="rounded-md border border-semantic-danger/30 bg-semantic-danger/10 px-3 py-2 text-[12px] text-semantic-danger">
              {errorMsg}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="leo-cta-primary mt-1.5"
          >
            {submitting ? "登录中..." : "登 录"}
          </button>
        </form>

        <div className="mt-6 text-center text-[10.5px] text-text-muted-weak">
          © Ad-Tech · 内部自动化投放系统
        </div>
      </div>
    </div>
  );
}